import { COLOR_TOKENS, type ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';

import { Box, type SxProps, type Theme } from '@mui/material';

const VIEWBOX_WIDTH = 640;
const VIEWBOX_HEIGHT = 260;
const PADDING = { top: 16, right: 16, bottom: 28, left: 16 };
// leaves room above the tallest point so the line never touches the top edge
const HEADROOM = 1.1;
// room for the widest tick label, and the gap between a label and the plot
const LABEL_GUTTER = 34;
const LABEL_GAP = 6;
const TICK_FONT_SIZE = 12;

export type LineChartSeries = Readonly<{
  id: string;
  points: readonly number[];
  color: ColorToken;
}>;

export type LineChartProps = Readonly<{
  series: readonly LineChartSeries[];
  ariaLabel: string;
  gridLineCount?: number;
  strokeWidth?: number;
  /** Label each grid line with its value, so the magnitude can be read. */
  showValues?: boolean;
  /** Radius of the marker drawn at each point. 0 draws none. */
  pointRadius?: number;
  /** Smallest value the top grid line may take, so sparse data is not stretched to fill the chart. */
  minScale?: number;
}>;

const plotHeight = VIEWBOX_HEIGHT - PADDING.top - PADDING.bottom;

/**
 * The top of the scale. Labelled charts round up to a whole number of steps, so
 * every grid line falls on an integer instead of a fraction of the peak.
 */
const scaleTop = (peak: number, gridLineCount: number, labelled: boolean): number => {
  if (!labelled) return peak * HEADROOM || 1;

  return Math.max(1, Math.ceil(peak / gridLineCount)) * gridLineCount;
};

const toCoordinates = (
  points: readonly number[],
  maxValue: number,
  left: number,
  width: number,
): readonly (readonly [number, number])[] => {
  const step = points.length > 1 ? width / (points.length - 1) : 0;

  return points.map((value, index) => [
    left + step * index,
    PADDING.top + plotHeight * (1 - (maxValue > 0 ? value / maxValue : 0)),
  ]);
};

/** Multi-series line chart with horizontal grid lines and no x-axis. */
export const LineChart = ({
  series,
  ariaLabel,
  gridLineCount = 4,
  strokeWidth = 2.5,
  showValues = false,
  pointRadius = 0,
  minScale = 0,
}: LineChartProps) => {
  const peak = Math.max(...series.flatMap((line) => [...line.points]), minScale, 0);
  const maxValue = scaleTop(peak, gridLineCount, showValues);
  const left = showValues ? PADDING.left + LABEL_GUTTER : PADDING.left;
  const plotWidth = VIEWBOX_WIDTH - PADDING.right - left;

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
      sx={svgSx}
      role="img"
      aria-label={ariaLabel}
    >
      {Array.from({ length: gridLineCount + 1 }).map((_, index) => {
        const y = PADDING.top + (plotHeight / gridLineCount) * index;

        return (
          <g key={`grid-${index}`}>
            <line
              x1={left}
              x2={VIEWBOX_WIDTH - PADDING.right}
              y1={y}
              y2={y}
              stroke={COLOR_TOKENS['Colors/Border/border-secondary']}
              strokeWidth={1}
            />
            {showValues ? (
              <text
                x={left - LABEL_GAP}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                fill={COLOR_TOKENS['Colors/Text/text-tertiary']}
                fontSize={TICK_FONT_SIZE}
              >
                {(maxValue - (maxValue / gridLineCount) * index).toLocaleString()}
              </text>
            ) : null}
          </g>
        );
      })}
      {series.map((line) => {
        const coordinates = toCoordinates(line.points, maxValue, left, plotWidth);

        return (
          <g key={line.id}>
            <polyline
              points={coordinates.map(([x, y]) => `${x},${y}`).join(' ')}
              fill="none"
              stroke={COLOR_TOKENS[line.color]}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {pointRadius > 0
              ? coordinates.map(([x, y], index) => (
                  <circle
                    key={index}
                    cx={x}
                    cy={y}
                    r={pointRadius}
                    fill={COLOR_TOKENS[line.color]}
                  />
                ))
              : null}
          </g>
        );
      })}
    </Box>
  );
};

const svgSx: SxProps<Theme> = {
  width: '100%',
  height: 'auto',
};
